import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  arrayOf,
  bool,
  extend,
  params,
  QueryContext,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { Button } from '@thd-olt-component-react/button';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { PIPRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { AugmentedReality } from '@thd-olt-component-react/augmented-reality';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { EducationalSummary } from '@thd-olt-component-react/educational-summary';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { Col, Row } from '@thd-olt-component-react/grid';
import { HdhomeCatalogPromo } from '@thd-olt-component-react/hdhome-catalog-promo';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { MeasurementGuide } from '@thd-olt-component-react/measurement-guide';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { Price } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { ProductOverviewDesktop, QuickLinks } from '@thd-olt-component-react/product-overview';
import { QuestionsAndAnswersDesktop } from '@thd-olt-component-react/questions-and-answers';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { ReturnsMessaging } from '@thd-olt-component-react/returns-messaging';
import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { SeoLinks } from '@thd-olt-component-react/seo-links';
import { SpecificationsDesktop } from '@thd-olt-component-react/specifications';
import { Link, Links, StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { TextAnExpert } from '@thd-olt-component-react/text-an-expert';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { DynamicRecs } from '@thd-olt-component-react/thd-recs-containers';
import { RecsContext } from '~/@thd-olt-component-react/recommendations/dist/components/RecsContext';
import {
  CollectionList,
  CollectionListLoad,
  CollectionListModel,
  FeatureBasedRecommendations,
  FeatureBasedRecommendationsLoad,
  FeatureBasedRecommendationsModel,
  ItemRelatedGroups,
  ItemRelatedGroupsLoad,
  ItemRelatedGroupsModel,
  RatingsAndReviews,
  RatingsAndReviewsLoad,
  RatingsAndReviewsModel
} from './dynamic-imports';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayRequiredItems,
  shouldDisplayVisuallySimilar,
  shouldDisplayCompleteTheLook
} from './product-utils';
import { requestKey } from '../../constants';
import '../../styles/product-page.scss';

export const Product = () => {
  declareContexts([RecsContext, QueryContext, ExperienceContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { channel, segment = '', isDebugMode } = ctx;
  const arRef = useRef(null);
  const { itemId } = useParams();
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const marqueeRef = React.createRef();
  const marqueeRowRef = React.createRef();
  const resx = {
    appId: 'homedepot01',
    carouselOptions: {
      fullScrollSkip: 2
    },
    criteo: {
      version: 'model2'
    },
    card: true,
    event: 'product',
    maxProducts: 16,
    products: itemId,
    showATC: true
  };

  const [recommendationsState, setRecommendationsState] = useState({
    ready: false,
    schema: {
      pipInStock: false,
      pipSem: false,
      pipSponsored: true,
      required_items: false,
      visuallySimilar_1_0: false,
      completeTheLook: false
    }
  });

  const handleARClick = () => {
    arRef.current.click();
  };

  const organicSearchSchema = 'pipsem';

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  useEffect(() => {
    if (productData) {
      setRecommendationsState({
        ready: true,
        schema: {
          ...recommendationsState.schema,
          pipInStock: shouldDisplayPipInStock(productData),
          pipSem: shouldDisplayReferrerRecommendations(),
          required_items: shouldDisplayRequiredItems(productData),
          visuallySimilar_1_0: shouldDisplayVisuallySimilar(productData),
          completeTheLook: shouldDisplayCompleteTheLook(productData)
        }
      });
    }
  }, [productData]);

  const seeMoreDetailsSectionClick = () => {
    if (typeof window !== 'undefined') {
      if (document.getElementById('product-overview') === null) {
        window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        setTimeout(() => {
          window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        }, 2000);
      } else {
        window.scrollTo({ top: document.getElementById('product-overview').offsetTop, behavior: 'smooth' });
      }
    }
  };

  return (
    <LayoutManager
      name="pip-wrapper"
      useContextForProps
    >
      <LayoutGroup name="redirector">
        <PIPRedirector
          disableCanonicalRedirect
          disableCMMMCRedirect
        />
      </LayoutGroup>
      <LayoutGroup name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </LayoutGroup>
      <LayoutGroup name="pip-content">
        <RecommendationsProvider
          ready={recommendationsState.ready}
          resx={resx}
        >
          <Row className="isBound pip-page">
            <LayoutManager
              data
              input={{
                channel,
                isDebugMode,
                itemId,
                segment
              }}
              name="pip-master"
              useContextForProps
            >
              <LayoutGroup
                name="sticknav"
                tag={Row}
              >
                <StickyNav itemId={itemId}>
                  <Links>
                    <Link href="#product-section-overview">Product Overview</Link>
                    <Link href="#specifications">Specifications</Link>
                    <Link href="#product-section-qa">Questions & Answers</Link>
                    <Link href="#product-section-rr">Customer Reviews</Link>
                  </Links>
                </StickyNav>
              </LayoutGroup>
              <LayoutGroup
                className="u--paddingTop u--paddingBottom"
                name="breadcrumb-sharebelt-grp"
                tag={Row}
              >
                <LayoutManager
                  name="breadcrumb-sharebelt"
                  useContextForProps
                >
                  <LayoutGroup
                    flatten
                    name="breadcrumbs"
                    sm="10"
                    tag={Col}
                  >
                    <Breadcrumbs
                      itemId={itemId}
                    />
                  </LayoutGroup>
                  <LayoutGroup
                    flatten
                    name="sharebelt"
                    sm="2"
                    tag={Col}
                  >
                    <Sharebelt
                      horizontal
                      itemId={itemId}
                    >
                      <ShareBeltShareButton email />
                      <ShareBeltPrint />
                    </Sharebelt>
                  </LayoutGroup>
                </LayoutManager>
              </LayoutGroup>
              {(recommendationsState.schema.pipSem)
              && (
                <LayoutGroup
                  id="recs:organic_search"
                  name="recs:organic_search"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder
                      hideRecommendation
                      scheme={organicSearchSchema}
                    />
                  </Col>
                </LayoutGroup>
              )}
              <LayoutGroup
                name="zone-a"
                tag={Row}
              >
                <Col nopadding>
                  <Row forwardedRef={marqueeRowRef}>
                    <Col>
                      <Discontinued itemId={itemId} />
                    </Col>
                    <Col
                      className="sticky"
                      flatten
                      sm="7"
                    >
                      <div
                        className="zone-card overflow"
                        ref={marqueeRef}
                      >
                        <LayoutManager
                          name="product-details-share"
                          useContextForProps
                        />
                        <LayoutGroup name="product-info-bar">
                          <Row>
                            <ProductInfoBar
                              itemId={itemId}
                              padded
                            />
                          </Row>
                        </LayoutGroup>
                        <LayoutGroup
                          className="product-details__media"
                          sm="12"
                          tag={Col}
                        >
                          <MediaGalleryDesktop
                            highRes
                            hover
                            itemId={itemId}
                            marqueeRef={marqueeRef}
                            marqueeRowRef={marqueeRowRef}
                            onMediaClick={handleARClick}
                            showLoader
                          />
                        </LayoutGroup>
                        <LayoutGroup name="aisle-bay">
                          <Row>
                            <Col
                              className="aisle-bay u__text-align--center"
                              nopadding
                            >
                              <AisleBay itemId={itemId} />
                            </Col>
                          </Row>
                        </LayoutGroup>
                      </div>
                    </Col>
                    <Col
                      flatten
                      sm="5"
                    >
                      <div className="zone-card overflow">
                        <Row>
                          <LayoutManager
                            input={{
                              channel,
                              segment
                            }}
                            name="buy-area"
                            useContextForProps
                          >
                            <LayoutGroup
                              name="product-details-share"
                              tag={Row}
                            >
                              <LayoutManager
                                name="product-details-share"
                                useContextForProps
                              >
                                <LayoutGroup
                                  name="product-details"
                                  sm="12"
                                  tag={Col}
                                >
                                  <ProductDetails itemId={itemId}>
                                    <ProductDetails.Badge itemId={itemId} />
                                    <div className="product-details__badge-title--wrapper">
                                      <ProductDetails.Title />
                                    </div>
                                    <ProductDetails.BrandCollection showCollection />
                                    <div className="product-details__favorites">
                                      <Sharebelt itemId={itemId}>
                                        <ShareFavorite
                                          showCircle
                                          showFavoritesCount
                                        />
                                      </Sharebelt>
                                    </div>
                                    <ProductDetails.Ratings
                                      offsetselector="#sticky-nav"
                                      qaselector="#product-section-qa"
                                      showBadge
                                      target="#product-section-rr"
                                    />
                                  </ProductDetails>
                                </LayoutGroup>
                              </LayoutManager>
                            </LayoutGroup>
                            <LayoutGroup
                              name="salient"
                              tag={Col}
                            >
                              <SalientPoints
                                itemId={itemId}
                                max={3}
                              >
                                <SalientListItem>
                                  <Button
                                    className="see-details u__default-link"
                                    link
                                    onClick={seeMoreDetailsSectionClick}
                                  >
                                    See More Details
                                  </Button>
                                </SalientListItem>
                              </SalientPoints>
                            </LayoutGroup>
                            <LayoutGroup
                              name="measurement-guide"
                              tag={Col}
                            >
                              <MeasurementGuide />
                            </LayoutGroup>
                            <LayoutGroup
                              name="price"
                              tag={Col}
                            >
                              <Price
                                channel="desktop"
                                disableRangePricing
                                displayEachUom={false}
                                itemId={itemId}
                                large
                                skip
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              name="consumer-card-messaging"
                              tag={Col}
                            >
                              <ConsumerCardMessaging itemId={itemId} />
                            </LayoutGroup>
                            <LayoutGroup
                              name="gcc"
                              tag={Col}
                            >
                              <GlobalCustomConfigurator itemId={itemId} />
                            </LayoutGroup>
                            <LayoutGroup
                              name="super-sku"
                              tag={Col}
                            >
                              <SuperSku
                                itemId={itemId}
                                onChange={onChange}
                                shouldSyncAttributes
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              name="promotion-products"
                              tag={Col}
                            >
                              <PromotionProducts
                                itemId={itemId}
                              />
                            </LayoutGroup>
                            <LayoutGroup
                              name="order-sample"
                              tag={Col}
                            >
                              <OrderSample itemId={itemId}>
                                Need a closer look?
                              </OrderSample>
                            </LayoutGroup>
                            <LayoutGroup
                              name="buybox"
                              tag={Col}
                            >
                              <Buybox
                                itemId={itemId}
                                lmUseContextForProps
                                sticky
                              />
                            </LayoutGroup>
                          </LayoutManager>
                        </Row>
                      </div>
                      <LayoutManager
                        input={{
                          channel,
                          segment
                        }}
                        name="returns-area"
                        useContextForProps
                      >
                        <LayoutGroup name="returns-messaging">
                          <ReturnsMessaging itemId={itemId} />
                        </LayoutGroup>
                      </LayoutManager>
                    </Col>
                  </Row>
                </Col>
              </LayoutGroup>
              <LayoutGroup
                name="measurement-guide"
                tag={Row}
              >
                <div className="zone-card full-bleed">
                  <MeasurementGuide />
                </div>
              </LayoutGroup>
              <LayoutGroup
                name="product-highlights"
                tag={Row}
              >
                <div className="zone-card">
                  <Col xs="5">
                    <MediaGalleryDesktop
                      highlight
                      itemId={itemId}
                    />
                  </Col>
                  <Col xs="4">
                    <SalientPoints
                      fallbackToDescription
                      itemId={itemId}
                      max={5}
                      title="Highlights"
                    />
                    <KeyProductFeatures
                      displaySimilarLinks
                      hideImage
                      itemId={itemId}
                      maxfeatures={4}
                      twoColumnGrid
                    />
                  </Col>
                  <Col xs="3">
                    <QuickLinks itemId={itemId} />
                  </Col>
                </div>
              </LayoutGroup>
              <LayoutGroup
                name="augmented-reality"
                tag={Row}
              >
                <div className="zone-card">
                  <AugmentedReality
                    forwardedRef={arRef}
                    instructions
                    itemId={itemId}
                  />
                </div>
              </LayoutGroup>
              {recommendationsState.schema.pipInStock && (
                <LayoutGroup
                  name="pipinstock"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder scheme="pipinstock" />
                  </Col>
                </LayoutGroup>
              )}
              {(recommendationsState.schema.required_items) && (
                <LayoutGroup
                  name="required_items"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder
                      defer
                      scheme="required_items"
                    />
                  </Col>
                </LayoutGroup>
              )}
              <DynamicComponent
                pageType="pip"
              >
                <Hydrator
                  className="grid"
                  delay={1500}
                  id="product-section-fbt"
                  placeholder={(
                    <LoadingPlaceholder
                      color="#f5f5f5"
                      ready={false}
                      style={{ height: 750 }}
                      type="rect"
                    >
                      <div />
                    </LoadingPlaceholder>
                  )}
                  preserveCtxVal="clientStore"
                  scrollBuffer={100}
                >
                  <LayoutGroup
                    name="fbt"
                    tag={Row}
                  >
                    <div className="zone-card">
                      <FrequentlyBoughtTogether
                        itemId={itemId}
                        openInNewTab
                      />
                    </div>
                  </LayoutGroup>
                </Hydrator>
              </DynamicComponent>
              <DynamicComponent
                pageType="pip"
              >
                <>
                  <Hydrator
                    className="grid"
                    delay={1500}
                    id="product-section-key-feat"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  >
                    <LayoutGroup
                      name="specifications"
                      tag={Row}
                    >
                      <div className="zone-card">
                        <SpecificationsDesktop itemId={itemId}>
                          <KeyProductFeatures
                            itemId={itemId}
                          />
                        </SpecificationsDesktop>
                      </div>
                    </LayoutGroup>
                    <LayoutGroup
                      name="educational"
                      tag={Row}
                    >
                      <EducationalSummary itemId={itemId} />
                    </LayoutGroup>
                  </Hydrator>
                  <Hydrator
                    className="grid"
                    delay={1500}
                    id="product-section-collections"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={CollectionListLoad}
                  >
                    <LayoutGroup
                      name="collections"
                      tag={Row}
                    >
                      <div className="zone-card overflow">
                        <CollectionList
                          itemId={itemId}
                          qctx={qctx}
                        />
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                  <LayoutGroup
                    name="text-an-expert"
                    tag={Row}
                  >
                    <TextAnExpert />
                  </LayoutGroup>
                  <Hydrator
                    className="grid"
                    delay={1500}
                    id="product-section-overview"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      />
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  >
                    <LayoutGroup
                      id="product-overview"
                      name="product-overview"
                      tag={Row}
                    >
                      <div className="zone-card">
                        <ProductOverviewDesktop
                          channel={channel}
                          itemId={itemId}
                          showReturnMessage
                          revJet
                        />
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                </>
              </DynamicComponent>
              {(recommendationsState.ready && !recommendationsState.schema.pipInStock) && (
                <LayoutGroup
                  name="alternatives"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder
                      defer
                      scheme="pip_alternatives"
                    />
                  </Col>
                </LayoutGroup>
              )}
              <LayoutGroup
                name="pip_sponsored"
                tag={Row}
              >
                <Col nopadding>
                  <RecommendationsPlaceholder
                    defer
                    scheme="pip_sponsored"
                  />
                </Col>
              </LayoutGroup>
              <DynamicComponent
                pageType="pip"
              >
                <Hydrator
                  className="grid"
                  delay={1500}
                  id="product-section-fbr"
                  placeholder={(
                    <LoadingPlaceholder
                      color="#f5f5f5"
                      ready={false}
                      style={{ height: 750 }}
                      type="rect"
                    >
                      <div />
                    </LoadingPlaceholder>
                  )}
                  preserveCtxVal="clientStore"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  waitFor={FeatureBasedRecommendationsLoad}
                >
                  <LayoutGroup
                    name="FBR"
                    tag={Row}
                  >
                    <div className="zone-card">
                      <FeatureBasedRecommendations
                        hideOnMobile
                        itemId={itemId}
                        qctx={qctx}
                      />
                    </div>
                  </LayoutGroup>
                </Hydrator>
              </DynamicComponent>
              {(recommendationsState.schema.visuallySimilar_1_0) && (
                <LayoutGroup
                  name="visually-similar"
                  tag={Row}
                >
                  <Col className="zone-card">
                    <RecommendationsPlaceholder
                      defer
                      itemId={itemId}
                      scheme="visuallysimilar_1_0"
                    />
                  </Col>
                </LayoutGroup>
              )}
              {(recommendationsState.schema.completeTheLook) && (
                <LayoutGroup
                  name="complete-the-look"
                  tag={Row}
                >
                  <Col nopadding className="zone-card">
                    <DynamicRecs
                      schemaName="ctl"
                      anchor={itemId}
                      collectionUrl={productData?.details?.collection?.url}
                      requestKey={requestKey}
                      showLoading
                      hideFulfillment
                    />
                  </Col>
                </LayoutGroup>
              )}
              <DynamicComponent
                pageType="pip"
              >
                <>
                  <Hydrator
                    className="grid"
                    delay={1500}
                    id="product-section-irg"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    preserveCtxVal="clientStore"
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={ItemRelatedGroupsLoad}
                  >
                    <LayoutGroup
                      name="IRG"
                      tag={Row}
                    >
                      <div className="zone-card">
                        <Col>
                          <ItemRelatedGroups
                            accordion={false}
                            dsNext
                            itemId={itemId}
                            maxItems={7}
                            qctx={qctx}
                            rowHeader="Get Everything You Need"
                          />
                        </Col>
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                  <Hydrator
                    className="grid"
                    delay={2000}
                    id="product-section-qa"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={RatingsAndReviewsLoad}
                  >
                    <LayoutGroup
                      name="email-promo"
                      tag={Row}
                    >
                      <div className="zone-card">
                        <HdhomeCatalogPromo formSubmission />
                      </div>
                    </LayoutGroup>
                    <LayoutGroup
                      className="zone-card"
                      id="questions_answers_full"
                      name="questions"
                      tag={Row}
                    >
                      <QuestionsAndAnswersDesktop itemId={itemId} />
                    </LayoutGroup>
                  </Hydrator>
                  <Hydrator
                    className="grid"
                    delay={2000}
                    id="product-section-rr"
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={RatingsAndReviewsLoad}
                  >
                    <LayoutGroup
                      id="ratings-and-reviews"
                      name="ratings"
                      tag={Row}
                    >
                      <div className="zone-card">
                        <RatingsAndReviews
                          itemId={itemId}
                          prosAndCons
                          qctx={qctx}
                        />
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                </>
              </DynamicComponent>
              <LayoutGroup
                name="recently-viewed"
                tag={Row}
              >
                <Col nopadding>
                  <RecommendationsPlaceholder
                    defer
                    scheme="rv_gm_pip_rr"
                  />
                </Col>
              </LayoutGroup>
              <DynamicComponent
                pageType="pip"
              >
                <Hydrator
                  className="grid"
                  delay={2000}
                  id="product-section-seo"
                  placeholder={(
                    <LoadingPlaceholder
                      color="#f5f5f5"
                      ready={false}
                      style={{ height: 750 }}
                      type="rect"
                    >
                      <div />
                    </LoadingPlaceholder>
                  )}
                  preserveCtxVal="clientStore"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                >
                  <LayoutGroup
                    name="related-search"
                    tag={Row}
                  >
                    <Col nopadding>
                      <RelatedSearch itemId={itemId} />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup
                    name="seo-linksf"
                    tag={Row}
                  >
                    <SeoLinks path={pathname} />
                  </LayoutGroup>
                </Hydrator>
              </DynamicComponent>
            </LayoutManager>
          </Row>
        </RecommendationsProvider>
      </LayoutGroup>
    </LayoutManager>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {};

Product.dataModel = extend(
  {
    clientOnlyProduct: params({ itemId: string().isRequired })
      .shape({
        itemId: string(),
        dataSources: string(),
        info: shape({
          recommendationFlags: shape({
            reqItems: bool(),
            visualNavigation: bool()
          }),
          returnable: string()
        }),
        availabilityType: shape({
          discontinued: bool(),
          type: string()
        }),
        fulfillment: params({ storeId: string() })
          .shape({
            backordered: bool(),
            fulfillmentOptions: arrayOf(shape({
              type: string(),
              fulfillable: bool(),
              services: arrayOf(shape({
                type: string(),
                locations: arrayOf(shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                }))
              }))
            }))
          })
      })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired })
      .shape({
        seoDescription: string()
      })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  RecommendationsPlaceholder,
  Discontinued,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  ConsumerCardMessaging,
  OrderSample,
  SuperSku,
  Buybox,
  AugmentedReality,
  FrequentlyBoughtTogether,
  ProductOverviewDesktop,
  PromotionProducts,
  FeatureBasedRecommendationsModel,
  ItemRelatedGroupsModel,
  SpecificationsDesktop,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsModel,
  SeoLinks,
  RelatedSearch,
  CollectionListModel,
  GlobalCustomConfigurator
);
